import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import AdvancedSearch from "../../components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria, stripAdvancedSearchFilters } from "@utils/searchHelpers";
import queryKeys from "../../configs/react-query/query-key";
import { useMutationData } from "../../hooks/useMutationData";
import { useSearchInfo } from "../../hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime, formatDateForRequest, formatVerificationType, translateDepartment } from "../../utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { AdjustIcon } from "../Icons";
import TableWithPagination from "../Tables/TableWithPagination";
import TabsContainer from "../Tabs/TabsContainer";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { LOCAL_STORAGE, SCAN_TYPE_OPTIONS } from "@constants/index";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { useVerificationTypeOptions } from "@hooks/useVerificationTypeOptions";
import useEscapeKey from "@hooks/useEscapeKey";

const INITIAL_PREVIEW_SEARCH_CRITERIA = {
  createdAtFrom: "",
  createdAtTo: "",
  createrId: "",
};

const INITIAL_PROJECT_SEARCH_CRITERIA = {
  productName: "",
  createrDeptCd: "",
  creator: "",
  completionRequestDt: "",
  createdAtFrom: "",
  createdAtTo: "",
};

// Tab content components - moved completely outside to prevent re-creation
const PreviewTabContent = ({ isOpen, activeTab, onClose, onSelect, selectedVerificationResult, excludedVerificationResult = null, creatorList, dataTestId }) => {
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const { t } = useTranslation();
  const { colors } = useTheme();

  // Advanced search fields
  const previewAdvancedSearchFields = [
    {
      name: "createrId",
      label: t("common.creator"),
      placeholder: t("common.creator"),
      type: "select",
      keepRawLabel: true,
      options: creatorList,
      displayFormatLabelWithID: true,
    },
    {
      name: "createdAtFrom",
      type: "date",
      placeholder: t("common.create-date-from"),
    },
    {
      name: "createdAtTo",
      type: "date",
      placeholder: t("common.create-date-to"),
    },
  ];

  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: previewAdvancedSearchFields,
    onPanelClose: () => {
      setSearchPreviewCriteria((prev) => {
        const stripped = stripAdvancedSearchFilters(prev, {
          searchFields: previewAdvancedSearchFields,
          quickSearchField: "projectPreviewName",
          initialCriteria: INITIAL_PREVIEW_SEARCH_CRITERIA,
        });
        const newCriteria = search ? { ...stripped, projectPreviewName: search } : stripped;
        setCurrentPreviewPage(1);
        fetchPreviewData(newCriteria);
        return newCriteria;
      });
    },
  });

  // Table configurations
  const previewTableColumns = ["previewName", "creator", "creationDate"];
  const previewTableColumnConfig = {
    previewName: {
      style: {
        width: "35%",
        whiteSpace: "normal",
        textAlign: "left",
        verticalAlign: "middle",
      },
      headerRender: () => t("project.project-preview-name"),
      customRender: (value, row) => <div>{row.projectPreviewName}</div>,
    },
    creator: {
      style: {
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
        verticalAlign: "middle",
      },
      headerRender: () => t("project.clarity.creator"),
      customRender: (value, row) => <UserNameWithId name={row.createrName} id={row.createrId} />,
    },
    creationDate: {
      style: {
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
        verticalAlign: "middle",
      },
      headerRender: () => t("project.clarity.creation-date"),
      customRender: (value, row) => <div>{row.createdAt ? formatDateTime(row.createdAt) : ""}</div>,
    },
  };
  // Preview List states
  const [search, setSearch] = useState("");

  const [previewList, setPreviewList] = useState([]);
  const [previewInitialized, setPreviewInitialized] = useState(false);
  const [totalPreviewData, setTotalPreviewData] = useState(0);
  const [totalPreviewPages, setTotalPreviewPages] = useState(0);
  const [searchPreviewCriteria, setSearchPreviewCriteria] = useState(INITIAL_PREVIEW_SEARCH_CRITERIA);
  const [currentPreviewPage, setCurrentPreviewPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimerRef = useRef(null);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.VERIFICATION_COMPARISONINFO_PREVIEW_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [localSelectedPreviewItem, setLocalSelectedPreviewItem] = useState(null);

  // Preview List API
  const getPreviewListData = useMutationData(
    queryKeys.getProjectPreviews,
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setPreviewList(response.object);
          setTotalPreviewData(response.objectCount);
          setTotalPreviewPages(Math.ceil(response.objectCount / itemsPerPage));
        }
      },
      onError: (error) => {
        console.error("Error during get preview data:", error);
      },
    },
  );

  // Fetch preview data
  const fetchPreviewData = (data) => {
    const effectiveData = resolveSearchCriteria(data, {
      quickSearchField: "projectPreviewName",
      isAdvancedPanelOpen: showAdvancedSearch,
    });
    const bodyRequest = Object.keys(effectiveData).reduce((acc, key) => {
      const value = effectiveData[key];
      if (value !== "" && value !== undefined && value !== null) {
        acc[key] = formatDateForRequest(value);
      }
      return acc;
    }, {});

    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPreviewPage - 1);
    getPreviewListData.mutate(bodyRequest);
  };

  // Load data when modal opens, tab changes, or pagination changes
  useEffect(() => {
    if (!isOpen || activeTab !== "preview") return;
    fetchPreviewData(searchPreviewCriteria);
    setPreviewInitialized(true);
  }, [isOpen, activeTab, currentPreviewPage, itemsPerPage, showAdvancedSearch]);

  // Handle search debouncing separately
  useEffect(() => {
    if (!isOpen || activeTab !== "preview" || !previewInitialized) return;
    setCurrentPreviewPage(1);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const newCriteria = { ...searchPreviewCriteria, projectPreviewName: search };
      setSearchPreviewCriteria(newCriteria);
      fetchPreviewData(newCriteria);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search]);

  // Initialize local selection when component mounts or when selectedVerificationResult changes
  useEffect(() => {
    if (isOpen && activeTab === "preview" && selectedVerificationResult?.projectPreviewSeq && !localSelectedPreviewItem) {
      setLocalSelectedPreviewItem(selectedVerificationResult);
    }
  }, [isOpen, activeTab, selectedVerificationResult, localSelectedPreviewItem]);

  // Reset selection when tab becomes inactive
  useEffect(() => {
    if (isOpen && activeTab !== "preview") {
      setLocalSelectedPreviewItem(null);
    }
  }, [isOpen, activeTab]);

  // Handle search input change
  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  // Handle preview search
  const handlePreviewSearch = (data) => {
    setCurrentPreviewPage(1);
    setSearchPreviewCriteria(data);
    fetchPreviewData(data);
  };

  // Handle preview row click
  const handlePreviewRowClick = (data) => {
    setLocalSelectedPreviewItem(data);
  };

  // Handle preview radio change
  const handlePreviewRadioChange = (selectedItems) => {
    if (selectedItems && selectedItems.length > 0) {
      // Find the full row data that matches the selected projectPreviewSeq
      const selectedPreviewSeq = selectedItems[0];
      const fullRowData = previewList.find(row => row.projectPreviewSeq === selectedPreviewSeq);
      setLocalSelectedPreviewItem(fullRowData);
    } else {
      setLocalSelectedPreviewItem(null);
    }
  };

  // Handle items per page change
  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  return (
    <div className="space-y-4">
      {/* Search Section */}
      <div className="flex items-center gap-4 max-w-xl">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon
            className={`absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform ${colors.textSecondary}`}
          />
          <input
            type="text"
            placeholder={t("project.verification.search-by-preview-name")}
            value={search}
            onChange={handleSearch}
            className={`w-full rounded-lg border px-3 py-2 pr-8 pl-10 text-sm focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
            data-testid={fieldInputTestId("search")}
          />
          {isSearching && (
            <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
              <div className={`h-3 w-3 animate-spin rounded-full border-b-2 ${colors.borderLoader}`}></div>
            </div>
          )}
        </div>
        <button
          onClick={handleAdvancedSearchToggle}
          className={`inline-flex items-center justify-center border px-4 py-2 ${colors.borderOutLineButton} rounded-md text-sm font-medium shadow-sm ${colors.textActive} ${colors.hoverOutlineModalSelection} whitespace-nowrap`}
          data-testid={fieldInputTestId("advanced-search-toggle")}
        >
          <AdjustIcon className="mr-2 h-4 w-4" />
          <span className="hidden sm:inline">{t("common.advanced-search")}</span>
        </button>
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <div className="relative z-30">
          <AdvancedSearch
            title={t("common.advanced-search")}
            searchFields={previewAdvancedSearchFields}
            searchHandler={handlePreviewSearch}
            withoutSearch={true}
            gridCols="md:grid-cols-2 lg:grid-cols-3"
            padding="p-4"
          />
        </div>
      )}

        <TableWithPagination
          columns={previewTableColumns}
          columnConfig={previewTableColumnConfig}
          data={previewList}
          currentPage={currentPreviewPage}
          setCurrentPage={setCurrentPreviewPage}
          loading={getPreviewListData.isPending || isSearching}
          rowKey="projectPreviewSeq"
          totalPages={totalPreviewPages}
          totalData={totalPreviewData}
          maxRowsPerPage={itemsPerPage}
          hasRadioButton={true}
          selectedRows={localSelectedPreviewItem ? [localSelectedPreviewItem.projectPreviewSeq] : []}
          disabledRow={excludedVerificationResult?.projectPreviewSeq != null ? [excludedVerificationResult] : []}
          onCheckboxChange={handlePreviewRadioChange}
          onRowClick={handlePreviewRowClick}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          showPagination={true}
          scrollY={showAdvancedSearch ? "360px" : "550px"}
          paddingYHeader="py-4"
          stickyHeader={true}
          showItemsPerPageSelector={true}
        />

      {/* Footer for Preview Tab */}
      <div className={`pt-4 ${colors.borderPrimary}`} data-testid={`${dataTestId}-footer`}>
        <div className="flex items-center justify-end">
          <div className="flex items-center space-x-3">
            <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={onClose}>
              {t("common.cancel-button")}
            </OutlineButton>
            <PrimaryButton
              data-testid={`${dataTestId}-confirm-button`}
              onClick={() => {
                if (localSelectedPreviewItem) {
                  onSelect(localSelectedPreviewItem);
                  onClose();
                }
              }}
              disabled={!localSelectedPreviewItem}
            >
              {t("common.select")}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProjectTabContent = ({ isOpen, activeTab, selectedVerificationResult, excludedVerificationResult = null, onClose, onSelect, creatorList, departmentList, productsList, scanToolList, dataTestId }) => {
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const { t } = useTranslation();
  const { colors } = useTheme();

  const projectTableColumns = [
    "verificationVersion",
    "realFileName",
    "verificationType",
    "projectId",
    "projectName",
    "creator",
    "creationDate"
  ];
  const projectTableColumnConfig = {
    verificationVersion: {
      headerRender: () => t("common.version"),
      style: { textAlign: "center", width: "110px" },
    },
    realFileName: {
      headerRender: () => t("project.verification.verification-file-name"),
    },
    verificationType: {
      headerRender: () => t("project.verification.verification-type"),
      customRender: (value) => formatVerificationType(value),
      style: { textAlign: "left" },
    },
    projectId: {
      headerRender: () => t("project.projectId"),
    },
    projectName: {
      headerRender: () => t("project.projectName"),
    },
    creator: {
      headerRender: () => t("project.creator"),
      customRender: (value, row) => <UserNameWithId name={row.createrName} id={row.createrId} />,
    },
    creationDate: {
      headerRender: () => t("project.createdAt"),
      customRender: (value, row) => <div>{row.createdAt ? formatDateTime(row.createdAt) : ""}</div>,
    },
  };

  const projectRowKey = ["projectId", "verificationVersion", "verificationType"];
  const { verificationTypeOptions, scanTypeOptions } = useVerificationTypeOptions({
    includeSBOM: true,
    scanTypeOptionsList: SCAN_TYPE_OPTIONS,
  });

  const projectAdvancedSearchFields = [
    {
      name: "verificationType",
      label: t("project.verification-type"),
      type: "select",
      placeholder: t("project.verification-type"),
      options: verificationTypeOptions,
    },
    { 
      name: "productSeq",
      label: t("project.productName"),
      type: "select",
      placeholder: t("project.productName"),
      options: productsList
    },
    // {
    //   name: "createrDeptCd",
    //   label: t("project.createrDeptCd"),
    //   type: "select",
    //   placeholder: t("project.createrDeptCd"),
    //   options: departmentList.map((item) => ({
    //     label: translateDepartment(t, item.value),
    //     value: item.value,
    //   })),
    // },
    {
      name: "createrId",
      label: t("project.creator"),
      type: "select",
      placeholder: t("project.creator"),
      options: creatorList,
      displayFormatLabelWithID: true,
    },
    {
      name: "createdAtFrom",
      type: "date",
      placeholder: t("common.create-date-from"),
    },
    {
      name: "createdAtTo",
      type: "date",
      placeholder: t("common.create-date-to"),
    },
  ];

  // Project List states
  const [search, setSearch] = useState("");

  const [projectList, setProjectList] = useState([]);
  const [projectInitialized, setProjectInitialized] = useState(false);
  const [localSelectedProjectItem, setLocalSelectedProjectItem] = useState(null);
  const [totalProjectData, setTotalProjectData] = useState(0);
  const [totalProjectPages, setTotalProjectPages] = useState(0);
  const [searchProjectCriteria, setSearchProjectCriteria] = useState(INITIAL_PROJECT_SEARCH_CRITERIA);
  const [currentProjectPage, setCurrentProjectPage] = useState(1);
  const [isProjectSearching, setIsProjectSearching] = useState(false);
  const projectDebounceTimerRef = useRef(null);
  const { itemsPerPage: projectItemsPerPage, handleItemsPerPageChange: handleProjectItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.VERIFICATION_COMPARISONINFO_PROJECT_LIST, DEFAULT_ITEMS_PER_PAGE);

  // Project List API
  const getProjectListData = useMutationData(
    queryKeys.getProjectVerificationData,
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setProjectList(response.object);
          setTotalProjectData(response.objectCount);
          setTotalProjectPages(Math.ceil(response.objectCount / projectItemsPerPage));
        }
      },
      onError: (error) => {
        console.error("Error during get project data:", error);
      },
    },
  );

  // Fetch project data
  const fetchProjectData = (data) => {
    const effectiveData = resolveSearchCriteria(data, {
      quickSearchField: "projectName",
      isAdvancedPanelOpen: showProjectAdvancedSearch,
    });
    const bodyRequest = Object.keys(effectiveData).reduce((acc, key) => {
      const value = effectiveData[key];
      if (value !== "" && value !== undefined && value !== null) {
        acc[key] = formatDateForRequest(value);
      }
      return acc;
    }, {});

    bodyRequest["pageSize"] = projectItemsPerPage;
    bodyRequest["startRow"] = projectItemsPerPage * (currentProjectPage - 1);
    getProjectListData.mutate(bodyRequest);
  };

  const { showAdvancedSearch: showProjectAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: projectAdvancedSearchFields,
    onPanelClose: () => {
      setSearchProjectCriteria((prev) => {
        const stripped = stripAdvancedSearchFilters(prev, {
          searchFields: projectAdvancedSearchFields,
          quickSearchField: "projectName",
          initialCriteria: INITIAL_PROJECT_SEARCH_CRITERIA,
        });
        const newCriteria = search ? { ...stripped, projectName: search } : stripped;
        setCurrentProjectPage(1);
        fetchProjectData(newCriteria);
        return newCriteria;
      });
    },
  });

  // Load data when modal opens, tab changes, or pagination changes
  useEffect(() => {
    fetchProjectData(searchProjectCriteria);
    setProjectInitialized(true);
  }, [isOpen, activeTab, currentProjectPage, projectItemsPerPage, showProjectAdvancedSearch]);

  // Handle search debouncing separately
  useEffect(() => {
    if (!isOpen || activeTab !== "project" || !projectInitialized) return;
    setCurrentProjectPage(1);

    if (projectDebounceTimerRef.current) {
      clearTimeout(projectDebounceTimerRef.current);
    }

    projectDebounceTimerRef.current = setTimeout(() => {
      const newCriteria = { ...searchProjectCriteria, projectName: search };
      setSearchProjectCriteria(newCriteria);
      fetchProjectData(newCriteria);
    }, 300);

    return () => {
      if (projectDebounceTimerRef.current) {
        clearTimeout(projectDebounceTimerRef.current);
      }
    };
  }, [search]);

  // Initialize local selection when component mounts or when selectedVerificationResult changes
  useEffect(() => {
    if (isOpen && activeTab === "project" && selectedVerificationResult?.projectId && !localSelectedProjectItem) {
      setLocalSelectedProjectItem(selectedVerificationResult);
    }
  }, [isOpen, activeTab, selectedVerificationResult, localSelectedProjectItem]);

  // Reset selection when tab becomes inactive
  useEffect(() => {
    if (isOpen && activeTab !== "project") {
      setLocalSelectedProjectItem(null);
    }
  }, [isOpen, activeTab]);

  // Handle search input change
  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  // Handle project search
  const handleProjectSearch = (data) => {
    setCurrentProjectPage(1);
    const newCriteria = { ...data, projectName: search };
    setSearchProjectCriteria(newCriteria);
    fetchProjectData(newCriteria);
  };

  // Handle project row click
  const handleProjectRowClick = (data) => {
    setLocalSelectedProjectItem(data);
  };

  // Handle project radio change
  const handleProjectRadioChange = (selectedItems) => {
    if (selectedItems && selectedItems.length > 0) {
      setLocalSelectedProjectItem(selectedItems[0]);
    } else {
      setLocalSelectedProjectItem(null);
    }
  };

  // Handle items per page change
  const handleProjectItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleProjectItemsPerPageChange(newItemsPerPage);
    setCurrentProjectPage(1); // Reset to page 1 when changing number of rows
  };

  return (
    <div className="space-y-4">
      {/* Search Section */}
      <div className="flex items-center gap-4 max-w-xl">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon
            className={`absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform ${colors.textSecondary}`}
          />
          <input
            type="text"
            placeholder={t("project.verification.search-by-project-name")}
            value={search}
            onChange={handleSearch}
            className={`w-full rounded-lg border px-3 py-2 pr-8 pl-10 text-sm focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
            data-testid={fieldInputTestId("search")}
          />
          {isProjectSearching && (
            <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
              <div className={`h-3 w-3 animate-spin rounded-full border-b-2 ${colors.borderLoader}`}></div>
            </div>
          )}
        </div>
        <button
          onClick={handleAdvancedSearchToggle}
          className={`inline-flex items-center justify-center border px-4 py-2 ${colors.borderOutLineButton} rounded-md text-sm font-medium shadow-sm ${colors.textActive} ${colors.hoverOutlineModalSelection} whitespace-nowrap`}
          data-testid={fieldInputTestId("advanced-search-toggle")}
        >
          <AdjustIcon className="mr-2 h-4 w-4" />
          <span className="hidden sm:inline">{t("common.advanced-search")}</span>
        </button>
      </div>

      {/* Advanced Search */}
      {showProjectAdvancedSearch && (
        <div className="relative z-30">
          <AdvancedSearch
            title={t("common.advanced-search")}
            searchFields={projectAdvancedSearchFields}
            searchHandler={handleProjectSearch}
            withoutSearch={true}
            padding="p-4"
          />
        </div>
      )}

      {/* Table */}
        <TableWithPagination
          columns={projectTableColumns}
          columnConfig={projectTableColumnConfig}
          data={projectList}
          currentPage={currentProjectPage}
          setCurrentPage={setCurrentProjectPage}
          loading={getProjectListData.isPending || isProjectSearching}
          rowKey={projectRowKey}
          totalPages={totalProjectPages}
          totalData={totalProjectData}
          maxRowsPerPage={projectItemsPerPage}
          hasRadioButton={true}
          selectedRows={localSelectedProjectItem ? [localSelectedProjectItem] : []}
          disabledRow={
            excludedVerificationResult?.projectId != null &&
            excludedVerificationResult?.verificationVersion != null &&
            excludedVerificationResult?.verificationType != null
              ? [excludedVerificationResult]
              : []
          }
          onCheckboxChange={handleProjectRadioChange}
          onRowClick={handleProjectRowClick}
          onItemsPerPageChange={handleProjectItemsPerPageChangeWithReset}
          showPagination={true}
          scrollY={showProjectAdvancedSearch ? "300px" : "550px"}
          paddingYHeader="py-4"
          stickyHeader={true}
          showItemsPerPageSelector={true}
        />

      {/* Footer for Project Tab */}
      <div className={`pt-4 ${colors.borderPrimary}`} data-testid={`${dataTestId}-footer`}>
        <div className="flex items-center justify-end">
          <div className="flex items-center space-x-3">
            <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={onClose}>
              {t("common.cancel-button")}
            </OutlineButton>
            <PrimaryButton
              data-testid={`${dataTestId}-confirm-button`}
              onClick={() => {
                if (localSelectedProjectItem) {
                  onSelect(localSelectedProjectItem);
                  onClose();
                }
              }}
              disabled={!localSelectedProjectItem}
            >
              {t("common.select")}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

const VerificationResultSelectionModal = ({ isOpen, onClose, onSelect, selectedVerificationResult = null, excludedVerificationResult = null, "data-testid": dataTestId = "verification-result-selection-modal" }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  // Tab state
  const [activeTab, setActiveTab] = useState("project"); // "preview" or "project"

  const { creatorList, departmentList, productsList, fetchSearchInfo, scanToolList } = useSearchInfo();

  const usePreview = (localStorage.getItem(LOCAL_STORAGE.USE_PREVIEW) !== undefined
    && localStorage.getItem(LOCAL_STORAGE.USE_PREVIEW) !== null) ? localStorage.getItem(LOCAL_STORAGE.USE_PREVIEW) : "N";

  // Tab configuration
  const tabs = [
    {
      label: t("verification-comparison.project-list"),
      content: <ProjectTabContent
        isOpen={isOpen}
        activeTab={activeTab}
        selectedVerificationResult={selectedVerificationResult}
        excludedVerificationResult={excludedVerificationResult}
        onClose={onClose}
        onSelect={onSelect}
        creatorList={creatorList}
        departmentList={departmentList}
        productsList={productsList}
        scanToolList={scanToolList}
        dataTestId={dataTestId}
      />
    }
  ];

  const tabsIncludePreview = [
    {
      label: t("verification-comparison.project-list"),
      content: <ProjectTabContent
        isOpen={isOpen}
        activeTab={activeTab}
        selectedVerificationResult={selectedVerificationResult}
        excludedVerificationResult={excludedVerificationResult}
        onClose={onClose}
        onSelect={onSelect}
        creatorList={creatorList}
        departmentList={departmentList}
        productsList={productsList}
        scanToolList={scanToolList}
        dataTestId={dataTestId}
      />
    },
    {
      label: t("verification-comparison.preview-list"),
      content: <PreviewTabContent
        isOpen={isOpen}
        activeTab={activeTab}
        onClose={onClose}
        onSelect={onSelect}
        selectedVerificationResult={selectedVerificationResult}
        excludedVerificationResult={excludedVerificationResult}
        creatorList={creatorList}
        dataTestId={dataTestId}
      />
    }
  ];



  // Initialize search info
  useEffect(() => {
    if (isOpen) {
      fetchSearchInfo();
    }
  }, [isOpen]);

  // Reset modal state when opened
  useEffect(() => {
    if (isOpen) {

      // Initialize selection based on selectedVerificationResult
      if (selectedVerificationResult) {
        if (selectedVerificationResult.projectPreviewSeq) {
          // It's a preview item
          setActiveTab("preview");
        } else if (selectedVerificationResult.projectSeq) {
          // It's a project item
          setActiveTab("project");
        }
      }
    }
  }, [isOpen, selectedVerificationResult]);

  // Handlers
  const handleTabChange = (tabIndex) => {
    const tab = tabIndex === 0 ? "project" : "preview";
    setActiveTab(tab);
    // Selection clearing is now handled by individual tabs
  };


  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start xl:justify-center p-4 backdrop-blur-[1px] overflow-x-auto`} data-testid={`${dataTestId}-overlay`}>
      <div className={`${colors.bgModal} w-[1200px] min-w-[1200px] h-fit flex-shrink-0 overflow-auto rounded-lg shadow-xl xl:overflow-y-hidden`} data-testid={dataTestId}>
        {/* Header */}
        <div className="px-6 py-4" data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>{t("verification-comparison.select-verification-project-version-title")}</h2>
          </div>
          <p className={`${colors.textSecondary} text-sm mt-1`}>
            {t("verification-comparison.select-verification-project-version-sub-title")}
          </p>
        </div>

        {/* Content with Tabs */}
        <div className="px-6 pb-4" data-testid={`${dataTestId}-content`}>
          <TabsContainer
            tabs={usePreview === "Y" ? tabsIncludePreview : tabs}
            defaultActiveTab={activeTab === "project" ? 0 : 1}
            onTabChange={handleTabChange}
          />
        </div>

        {/* Footer moved to individual tabs */}
      </div>
    </div>
  );
};

export default VerificationResultSelectionModal; 